import { Injectable } from '@angular/core';

import { ConfigurationService, Oauth2Service } from '@common';
import { Observable, from as observableFrom } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { BrowseContextService } from '../../context/child/browse-context.service';
import { ContextSerializedInterface } from '../../context/context-serialized.interface';
import { ContextStorageService } from '../../context/context-storage.service';
import { LocationService } from '../base/services/location.service';
import { BrowseConfigClass } from './child/browse-config.class';
import { ContextInterfaceBase } from './context.interface';

@Injectable({
    providedIn: 'root',
})
export class ContextServiceBase {
    public currentResolve: Observable<ContextServiceBase>;
    public errorsContexts: string[] = [];

    private contexts: ContextInterfaceBase[] = [];

    constructor(
        protected locationService: LocationService,
        protected contextStorageService: ContextStorageService,
        protected browseContextService: BrowseContextService,
        protected configService: ConfigurationService,
        protected oauth2: Oauth2Service,
    ) {}

    public get<T extends ContextInterfaceBase>(type: any): T {
        for (const i in this.contexts) {
            if (this.contexts[i] instanceof type) {
                return this.contexts[i] as T;
            }
        }
        return null;
    }

    /**
     * create intance of ContextServiceBase
     * @todo remove contextStorageService or add new prefix
     * @returns
     */
    public create(): ContextServiceBase {
        // eslint-disable-next-line max-len
        return new ContextServiceBase(
            this.locationService,
            this.contextStorageService,
            this.browseContextService,
            this.configService,
            this.oauth2,
        );
    }

    /**
     * Add new context or replace if same type exist in this.contexts
     * @param context
     * @returns
     */
    public setContext(context: ContextInterfaceBase): Observable<ContextServiceBase> {
        for (const i in this.contexts) {
            if (this.contexts[i] instanceof context.constructor) {
                this.contexts[i] = context;
                return context.updateContext(this);
            }
        }
        this.contexts.push(context);
        return context.updateContext(this);
    }

    /**
     *
     * Completely remove a context from Browsecontexts
     * @param context
     * @memberof ContextServiceBase
     */
    public removeContext(context: ContextInterfaceBase): void {
        const contextIndex: number = this.contexts.findIndex((ctx) => ctx instanceof context.constructor);
        if (this.contexts[contextIndex]) {
            this.contexts.splice(contextIndex);
        }
    }

    /**
     * Generate browse context if is not defined
     * @param route
     * @param state
     * @return
     */
    public resolve(): Observable<ContextServiceBase> {
        if (this.currentResolve) {
            return this.currentResolve;
        }
        if (this.contexts.length > 0) {
            observableFrom([this]);
        }
        this.initContext();
        this.currentResolve = this.initContext();
        return this.currentResolve;
    }

    public initContext(): Observable<ContextServiceBase> {
        return observableFrom([this]);
    }

    public serialize(): ContextSerializedInterface {
        const contextsSerialize: { [index: string]: any } = {};
        // eslint-disable-next-line guard-for-in
        for (const key in this.contexts) {
            contextsSerialize[this.contexts[key].keySerialize] = this.contexts[key].serialize();
        }
        return contextsSerialize as ContextSerializedInterface;
    }

    public unserialize(data: ContextSerializedInterface): Observable<ContextServiceBase> {
        const instance: ContextServiceBase = this.create();
        return this.browseContextService
            .create(Object.assign(new BrowseConfigClass(), data.BrowseContextService))
            .pipe(mergeMap((res) => instance.setContext(res).pipe(map(() => instance))));
    }
}
